import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { toast } from 'sonner';
import { humanizeError, GOOGLE_SIGNIN_USER_MESSAGE } from '../utils/errorHandling';
import {
  authPathWithPreservedPlan,
  consumePendingBillingPlan,
  persistBillingPlanFromUrlQuery,
} from '../utils/postAuthBillingPlan';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { AuthPageLayout } from '../components/layout/AuthPageLayout';

const TURNSTILE_SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();
const TURNSTILE_ENABLED = Boolean(TURNSTILE_SITE_KEY);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogleCredential } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    persistBillingPlanFromUrlQuery(searchParams.get('plan'));
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !turnstileToken) {
      toast.error('Please complete the security check before signing in.');
      return;
    }
    setLoading(true);
    try {
      await login(email, password, turnstileToken || undefined);
      toast.success('Welcome back!');
      persistBillingPlanFromUrlQuery(searchParams.get('plan'));
      const pendingPlan = consumePendingBillingPlan();
      if (pendingPlan) {
        navigate(`/billing/checkout?plan=${pendingPlan}`, { replace: true });
        return;
      }
      navigate(location.state?.returnTo || '/dashboard', { replace: true });
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setLoading(true);
    try {
      await loginWithGoogleCredential(credential);
      toast.success('Welcome back!');
      persistBillingPlanFromUrlQuery(searchParams.get('plan'));
      const pendingPlan = consumePendingBillingPlan();
      if (pendingPlan) {
        navigate(`/billing/checkout?plan=${pendingPlan}`, { replace: true });
        return;
      }
      navigate(location.state?.returnTo || '/dashboard', { replace: true });
    } catch (error) {
      toast.error(humanizeError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = (message) => {
    if (message && message !== GOOGLE_SIGNIN_USER_MESSAGE) {
      // eslint-disable-next-line no-console
      console.error('[MyLegacyBridge] Google sign-in error:', message);
    }
    toast.error(GOOGLE_SIGNIN_USER_MESSAGE);
  };

  return (
    <AuthPageLayout>
          <div className="mb-8 text-center">
            <Link to="/home" className="inline-flex flex-col items-center gap-3 transition-opacity hover:opacity-90">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#c9a961]/20 text-sm font-medium text-[#5c4a32] ring-1 ring-[#c9a961]/30">
                G
              </span>
              <span>
                <h1 className="text-[#1c1917]" data-testid="logo">
                  <BrandWordmark variant="auth" />
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-[#5c534a]">
                  Sign in to continue your family archive
                </p>
              </span>
            </Link>
          </div>

          <Card className="border border-[#e8e2d8]/90 bg-[#fffcf7]/95 shadow-[0_1px_0_rgba(28,25,23,0.04),0_12px_40px_-24px_rgba(28,25,23,0.12)] rounded-2xl">
            <CardHeader className="space-y-1 pb-2">
              <CardTitle className="font-serif text-xl font-normal text-[#1c1917]" data-testid="login-title">
                Sign in
              </CardTitle>
              <CardDescription className="text-[#5c534a]">Use the email and password for your account</CardDescription>
              {process.env.NODE_ENV === 'development' && (
                <p className="rounded-lg border border-dashed border-[#e8e2d8] bg-[#faf8f5]/80 px-3 py-2 text-left text-[11px] leading-relaxed text-[#5c534a]">
                  <span className="font-medium text-[#3d3833]">Local sign-in:</span> start the API (for example{' '}
                  <code className="rounded bg-[#efe8dd] px-1">.\scripts\restart-api.ps1</code>), set{' '}
                  <code className="rounded bg-[#efe8dd] px-1">DEMO_USER_ENABLED=true</code> in{' '}
                  <code className="rounded bg-[#efe8dd] px-1">backend\.env</code>, keep{' '}
                  <code className="rounded bg-[#efe8dd] px-1">REACT_APP_TURNSTILE_SITE_KEY</code> empty unless you
                  wire Turnstile, then use <code className="rounded bg-[#efe8dd] px-1">demo@example.com</code> /{' '}
                  <code className="rounded bg-[#efe8dd] px-1">DemoPass123!</code>. Add{' '}
                  <code className="rounded bg-[#efe8dd] px-1">http://localhost:3000</code> to your Google OAuth web
                  client &quot;Authorized JavaScript origins&quot; if you test Google here.
                </p>
              )}
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} data-testid="login-form">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#3d3833]">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="border-[#e8e2d8] bg-white/90"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label htmlFor="password" className="text-[#3d3833]">
                        Password
                      </Label>
                      <Link
                        to="/forgot-password"
                        className="text-xs font-medium text-[#5c4a32] underline decoration-[#c9a961]/40 underline-offset-2 hover:text-[#1c1917]"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="border-[#e8e2d8] bg-white/90"
                      data-testid="login-password-input"
                    />
                  </div>
                  <TurnstileWidget siteKey={TURNSTILE_SITE_KEY || null} onToken={setTurnstileToken} className="flex justify-center py-1" />
                  <Button
                    type="submit"
                    className="mt-2 w-full rounded-full bg-[#1c1917] text-[#faf7f2] hover:bg-[#2c2824]"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Signing in…' : 'Sign in'}
                  </Button>
                  <div className="pt-1">
                    <GoogleSignInButton
                      onCredential={handleGoogleCredential}
                      onError={handleGoogleError}
                      text="continue_with"
                      disabled={loading}
                    />
                  </div>
                </div>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col gap-3 border-t border-[#e8e2d8]/60 bg-[#faf8f5]/50 pt-6">
              <p className="text-center text-sm text-[#5c534a]">
                Need an account?{' '}
                <Link
                  to={authPathWithPreservedPlan('/register', searchParams.get('plan'))}
                  className="font-medium text-[#5c4a32] underline decoration-[#c9a961]/50 underline-offset-2 hover:text-[#1c1917]"
                  data-testid="register-link"
                >
                  Create one
                </Link>
              </p>
              <p className="text-center">
                <Link
                  to="/home"
                  className="text-xs text-[#8b7355] transition hover:text-[#5c4a32]"
                >
                  ← Back to home
                </Link>
              </p>
            </CardFooter>
          </Card>
    </AuthPageLayout>
  );
};

export default Login;